import React, { useEffect, useState } from "react";
import Styles from "./Element.module.css";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { decryptData } from "../../../../commons/Decode";
import { verifyUserName } from "../../../../Services/ApiCalling/Auth";

const UserDetails = ({ userData, handleData, handleProceed }) => {
  const initialState = {
    fullName: userData?.userDetails?.fullName || "",
    userName: userData?.userDetails?.userName || "",
    email: userData?.userDetails?.email || userData?.email || "",
    password: userData?.userDetails?.password || "",
    confirmPassword: userData?.userDetails?.confirmPassword || "",
    phone: userData?.userDetails?.phone || "",
  };

  const [formData, setFormData] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordC, setShowPasswordC] = useState(false);
  const [errors, setErrors] = useState({});

  const fieldDependencies = {
    userName: "fullName",
    phone: "userName",
    password: "phone",
    confirmPassword: "password",
  };

  const handleChange = (field, value) => {
    if (field === "userName") {
      value = value.replace(/[^a-zA-Z0-9_.]/g, "");
    }

    if (field === "phone") {
      value = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => {
      const updatedErrors = { ...prev };

      delete updatedErrors[field];
      const dependentField = fieldDependencies[field];
      if (dependentField) delete updatedErrors[dependentField];

      if (field === "phone" && value.length === 10) {
        delete updatedErrors.phone;
      }
      return updatedErrors;
    });
  };

  const validatePassword = (password) => {
    const errors = [];
    const isValid =
      password.length >= 8 &&
      password.length <= 20 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>_-]/.test(password);

    if (!isValid) {
      errors.push(
        "Password must be 8–12 characters with uppercase, lowercase, number & special symbol."
      );
    }
    if (/\s/.test(password)) errors.push("No spaces allowed.");
    return errors;
  };

  const validateFields = async () => {
    const requiredFields = [
      "fullName",
      "userName",
      "password",
      "confirmPassword",
      "phone",
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())} is required`;
      }
    });

    if (formData.userName) {
      if (formData.userName.length < 4) {
        newErrors.userName = "Username must be at least 4 characters long";
      } else {
        try {
          let response = await verifyUserName(formData.userName);
          const decryptedData = await decryptData(response?.data);
          
          if (
            decryptedData &&
            (decryptedData?.code !== 200 || decryptedData?.error)
          ) {
            newErrors.userName =
              decryptedData?.message ||
              decryptedData?.error ||
              "Username not available";
          }
        } catch (err) {
          newErrors.userName = "Could not verify username";
        }
      }
    }
    if (formData.phone) {
      if (formData.phone.length !== 10) {
        newErrors.phone = "Phone number must be exactly 10 digits";
      }
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors.join(" ");
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Password does not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async (data) => {
    const isValid = await validateFields();
    if (!isValid) return;

    const userDetails = {
      ...formData,
      roles: userData?.profile?.value,
      categoryId: formData.category,
    };

    // Persist user details for next visit
    handleData("userDetails", userDetails);
    handleProceed(data);
  };

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
    disabled = false
  ) => (
    <TextInput
      heading={`${heading} `}
      value={formData[field]}
      onChange={(value) => handleChange(field, value)}
      example={example}
      type={type}
      height="45"
      error={errors[field]}
      readOnly={readOnly}
      disabled={disabled}
    />
  );

  const renderPasswordInput = (field, placeholder) => (
    <div key={field} className={`${Styles.passCont} ${Styles.loginInputCont}`}>
      <div className={Styles.loginInputElemCOnt}>
        {field === "password" ? "Password " : "Confirm Password "}
      </div>
      <div className={Styles.inputFieldCont}>
        {field === "confirmPassword" &&
        formData.password === formData.confirmPassword ? (
          <CheckCircleIcon style={{ fontSize: "16px", color: "green" }} />
        ) : (
          <LockIcon style={{ fontSize: "16px", color: "#8C8A96" }} />
        )}
        <div className={Styles.inputField}>
          <input
            type={
              field === "password"
                ? showPassword
                  ? "text"
                  : "password"
                : showPasswordC
                ? "text"
                : "password"
            }
            placeholder={placeholder}
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
          />
        </div>
        <span
          onClick={() =>
            field === "password"
              ? setShowPassword(!showPassword)
              : setShowPasswordC(!showPasswordC)
          }
        >
          {field === "password" ? (
            showPassword ? (
              <VisibilityIcon style={{ fontSize: "16px", color: "#8C8A96" }} />
            ) : (
              <VisibilityOffIcon
                style={{ fontSize: "16px", color: "#8C8A96" }}
              />
            )
          ) : showPasswordC ? (
            <VisibilityIcon style={{ fontSize: "16px", color: "#8C8A96" }} />
          ) : (
            <VisibilityOffIcon style={{ fontSize: "16px", color: "#8C8A96" }} />
          )}
        </span>
      </div>
      {errors[field] && (
        <span className={`${Styles.anotherErr} ${Styles.error}`}>
          {errors[field]}
        </span>
      )}
    </div>
  );

  useEffect(() => {
    if (userData?.userDetails) {
      setFormData((prev) => ({
        ...prev,
        ...userData.userDetails,
      }));
    } else if (userData?.email) {
      setFormData((prev) => ({
        ...prev,
        email: userData.email,
      }));
    }
  }, [userData?.userDetails, userData?.email]);

  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={Styles.conterntHeader}>Admin User Details</div>
        <div className={Styles.detailsInpMainContainer}>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("fullName", "Full Name", "Eg. Your Name")}
            {renderTextInput("userName", "Username ", "Eg. gnaenergy1234")}
            {renderTextInput(
              "email",
              "Email ID",
              "Eg. gnaenergy1234",
              "text",
              false,
              true
            )}
          </div>
          <div className={Styles.layer3ElementsContainer}>
            {renderTextInput("phone", "Phone Number", "Eg. 9993567890")}
            {["password", "confirmPassword"].map((field) =>
              renderPasswordInput(field, "Eg. gnaenergy")
            )}
          </div>
        </div>
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
